import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../v32/components/ui/card';
import { Button } from '../v32/components/ui/button';
import { Badge } from '../v32/components/ui/badge';
import { Checkbox } from '../v32/components/ui/checkbox';
import { Separator } from '../v32/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../v32/components/ui/select';
import { ShoppingCart, Check, Trash2 } from 'lucide-react';
import { AppFrame } from '../v32/components/AppFrame';
import type { ShoppingItem, ShoppingList, Household } from './ShoppingList.container';

interface ShoppingListViewProps {
  // Data
  shoppingList: ShoppingList | null | undefined;
  groupedItems: Record<string, ShoppingItem[]> | null | undefined;
  totalCost: number;
  currentMealPlan: any;
  
  // State
  checkedItems: Set<string>;
  editing: boolean;
  
  // Computed values
  checkedItemsCount: number;
  totalItemsCount: number;
  hasShoppingList: boolean;
  
  // Event handlers
  onToggleItem: (itemId: string) => void;
  onMarkAllPurchased: () => void;
  onToggleEditing: () => void;
  onCompleteShopping: () => void;
  onGoToMealPlans: () => void;
}

/**
 * ShoppingList View - Pure UI component with no hooks or data fetching
 * 
 * This handles only:
 * - UI rendering based on props
 * - User interactions (forwarded to container via callbacks)
 * - Conditional rendering based on state
 * - No business logic, no side effects, no data fetching
 */
export default function ShoppingListView({
  // Data
  shoppingList,
  groupedItems,
  totalCost,
  currentMealPlan,
  
  // State
  checkedItems,
  editing,
  
  // Computed values
  checkedItemsCount,
  totalItemsCount,
  hasShoppingList,
  
  // Event handlers
  onToggleItem,
  onMarkAllPurchased,
  onToggleEditing,
  onCompleteShopping,
  onGoToMealPlans,
}: ShoppingListViewProps) {

  // No shopping list state
  if (!hasShoppingList) {
    return (
      <AppFrame>
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Shopping List</h3>
          <p className="text-gray-500 mb-6">Generate a meal plan to create your shopping list.</p>
          <Button onClick={onGoToMealPlans}>
            Go to Meal Plans
          </Button>
        </div>
      </AppFrame>
    );
  }

  return (
    <AppFrame>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Shopping List</h1>
        </div>

        {/* Main shopping list card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Shopping List {currentMealPlan?.weekRange ? `for ${currentMealPlan.weekRange}` : ''}
            </CardTitle>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-sm">
                {totalItemsCount} items
              </Badge>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">${totalCost.toFixed(2)}</span>
                <Button onClick={onMarkAllPurchased} size="sm" variant="outline">
                  <Check className="h-4 w-4 mr-1" />
                  Mark All Purchased
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Shopping list items grouped by category */}
            {Object.entries(groupedItems || {}).map(([category, items]) => (
              <div key={category} className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  {category}
                </h4>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={item.id}
                          checked={checkedItems.has(item.id)}
                          onCheckedChange={() => onToggleItem(item.id)}
                        />
                        <div>
                          <p className="font-medium text-gray-900">{item.ingredient}</p>
                          <p className="text-sm text-gray-500">{item.quantity} {item.unit}</p>
                        </div>
                      </div>
                      {item.estimatedPrice && (
                        <span className="text-sm font-medium text-gray-900">
                          ${item.estimatedPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <Separator />
            
            {/* Footer with actions */}
            <div className="flex justify-between items-center pt-4">
              <div className="text-sm text-gray-500">
                {checkedItemsCount} of {totalItemsCount} items purchased
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onToggleEditing}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  {editing ? 'Cancel' : 'Edit List'}
                </Button>
                <Button 
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={onCompleteShopping}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Complete Shopping
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppFrame>
  );
}
